import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CompactPicker } from "react-color";
import { useTranslation } from "react-i18next";
import { WASTE_FORM_INPUT_CLASS } from "./wasteFlowFormUi";

const ChevronDown = () => (
  <svg
    className="h-4 w-4 shrink-0 text-[#222222]"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const PICKER_ESTIMATED_HEIGHT = 120;
const PICKER_MARGIN = 4;
const COLOR_KEY_BY_HEX = {
  "#4D4D4D": "darkGray",
  "#999999": "gray",
  "#FFFFFF": "white",
  "#F44E3B": "red",
  "#FE9200": "orange",
  "#FCDC00": "yellow",
  "#DBDF00": "yellowGreen",
  "#A4DD00": "limeGreen",
  "#68CCCA": "turquoise",
  "#73D8FF": "lightBlue",
  "#AEA1FF": "lightPurple",
  "#FDA1FF": "lightPink",
  "#333333": "anthracite",
  "#808080": "mediumGray",
  "#CCCCCC": "lightGray",
  "#D33115": "brickRed",
  "#E27300": "darkOrange",
  "#FCC400": "mustard",
  "#B0BC00": "olive",
  "#68BC00": "green",
  "#16A5A5": "teal",
  "#009CE0": "blue",
  "#7B64FF": "purple",
  "#FA28FF": "fuchsia",
  "#000000": "black",
  "#666666": "slateGray",
  "#B3B3B3": "pearlGray",
  "#9F0500": "burgundy",
  "#C45100": "terracotta",
  "#FB9E00": "amber",
  "#808900": "khaki",
  "#194D33": "forestGreen",
  "#0C797D": "petrolBlue",
  "#0062B1": "royalBlue",
  "#653294": "plum",
  "#AB149E": "magenta",
};

const getColorDisplayText = (hex, t) => {
  const normalized = String(hex || "")
    .trim()
    .toUpperCase();
  if (!normalized) return "";
  const colorKey = COLOR_KEY_BY_HEX[normalized];
  if (!colorKey) return normalized;
  return t(`settingsWaste.colorName.${colorKey}`, normalized);
};

const WasteFlowColorSelect = ({
  value,
  onChange,
  ariaLabel,
  placeholder = "",
}) => {
  const { t } = useTranslation();
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  const hex = String(value || "").trim();
  const isEmpty = !hex;
  const displayText = isEmpty ? placeholder : getColorDisplayText(hex, t);

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const safety = 88;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp =
      spaceBelow < PICKER_ESTIMATED_HEIGHT + PICKER_MARGIN + safety;

    setMenuPosition({
      left: rect.left,
      top: openUp ? rect.top - PICKER_MARGIN : rect.bottom + PICKER_MARGIN,
      width: rect.width,
      openUp,
    });
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const handleOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (pickerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  const picker =
    open && menuPosition
      ? createPortal(
          <div
            ref={pickerRef}
            className={`waste-flow-color-select__picker waste-flow-color-select__picker--fixed${
              menuPosition.openUp ? " waste-flow-color-select__picker--up" : ""
            }`}
            style={{
              left: menuPosition.left,
              top: menuPosition.top,
              width: menuPosition.width,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CompactPicker
              color={isEmpty ? "#FFFFFF" : hex}
              onChange={(color) => {
                onChange(color.hex);
                setOpen(false);
              }}
            />
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="waste-flow-color-select">
      <button
        ref={triggerRef}
        type="button"
        className={`waste-flow-color-select__trigger ${WASTE_FORM_INPUT_CLASS}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
      >
        <span className="waste-flow-color-select__swatch" aria-hidden>
          <span
            className={`waste-flow-color-select__dot${isEmpty ? " waste-flow-color-select__dot--empty" : ""}`}
            style={isEmpty ? undefined : { backgroundColor: hex }}
          />
        </span>
        <span
          className={`waste-flow-color-select__text${isEmpty ? " waste-flow-color-select__text--placeholder" : ""}`}
        >
          {displayText}
        </span>
        <ChevronDown />
      </button>
      {picker}
    </div>
  );
};

export default WasteFlowColorSelect;
